import { Topic } from '../../grades/components/GradeLessonPage';

export const trigonometry: Topic = {
  id: 'trigonometry',
  title: 'Trigonometry',
  icon: 'ri-compass-3-line',
  color: 'violet',
  skills: [
    {
      id: 'right-triangle-trig',
      title: 'Right Triangle Trigonometry (SOH-CAH-TOA)',
      standards: ['ACT Math: Trigonometry', 'G.SRT.C.6', 'G.SRT.C.7'],
      explanation: 'Right triangle trigonometry uses the relationships between angles and side lengths in right triangles. The three primary trigonometric ratios are remembered by SOH-CAH-TOA: Sine = Opposite/Hypotenuse, Cosine = Adjacent/Hypotenuse, Tangent = Opposite/Adjacent. Given an angle θ in a right triangle, the opposite side is across from θ, the adjacent side is next to θ (but not the hypotenuse), and the hypotenuse is the longest side opposite the right angle. These ratios allow you to find missing sides when you know an angle and one side, or find missing angles when you know two sides. On the ACT, you will need to identify which ratio to use based on what information is given and what you need to find, set up the correct equation, and solve using algebra or a calculator. Remember that these ratios only work in right triangles. Common applications include finding heights, distances, and angles in real-world scenarios involving right triangles.',
      questions: [
        {
          question: 'In a right triangle, if angle A = 30° and the hypotenuse is 20, what is the length of the side opposite angle A?',
          solution: 'Step 1: Use sine ratio: sin(A) = opposite/hypotenuse\nStep 2: Substitute: sin(30°) = opposite/20\nStep 3: sin(30°) = 0.5\nStep 4: 0.5 = opposite/20\nStep 5: Multiply by 20: opposite = 10',
          answer: '10'
        },
        {
          question: 'In a right triangle, the side adjacent to angle B is 12 and the hypotenuse is 15. Find cos(B).',
          solution: 'Step 1: Use cosine ratio: cos(B) = adjacent/hypotenuse\nStep 2: Substitute: cos(B) = 12/15\nStep 3: Simplify: cos(B) = 4/5 or 0.8',
          answer: '4/5 or 0.8'
        },
        {
          question: 'In a right triangle, if tan(C) = 3/4, and the side adjacent to angle C is 8, what is the length of the opposite side?',
          solution: 'Step 1: Use tangent ratio: tan(C) = opposite/adjacent\nStep 2: Substitute: 3/4 = opposite/8\nStep 3: Cross multiply: 4(opposite) = 24\nStep 4: Divide by 4: opposite = 6',
          answer: '6'
        },
        {
          question: 'A right triangle has legs of length 5 and 12. What is sin(A), where A is the angle opposite the side of length 5?',
          solution: 'Step 1: Find hypotenuse using Pythagorean theorem: c² = 5² + 12² = 25 + 144 = 169, so c = 13\nStep 2: Use sine ratio: sin(A) = opposite/hypotenuse\nStep 3: sin(A) = 5/13',
          answer: '5/13'
        },
        {
          question: 'In a right triangle, if angle D = 45° and the adjacent side is 10, what is the length of the opposite side?',
          solution: 'Step 1: Use tangent ratio: tan(D) = opposite/adjacent\nStep 2: Substitute: tan(45°) = opposite/10\nStep 3: tan(45°) = 1\nStep 4: 1 = opposite/10\nStep 5: Multiply by 10: opposite = 10',
          answer: '10'
        },
        {
          question: 'A ladder makes a 60° angle with the ground. If the ladder is 18 feet long, how high up the wall does it reach?',
          solution: 'Step 1: The height is opposite the 60° angle, ladder length is hypotenuse.\nStep 2: Use sine: sin(60°) = height/18\nStep 3: sin(60°) = √3/2 ≈ 0.866\nStep 4: 0.866 = height/18\nStep 5: Multiply by 18: height ≈ 15.59 feet',
          answer: 'Approximately 15.59 feet or 9√3 feet'
        },
        {
          question: 'In a right triangle, the opposite side is 7 and the adjacent side is 24. Find tan(θ).',
          solution: 'Step 1: Use tangent ratio: tan(θ) = opposite/adjacent\nStep 2: Substitute: tan(θ) = 7/24',
          answer: '7/24'
        },
        {
          question: 'If cos(E) = 0.6 and the hypotenuse is 25, what is the length of the adjacent side?',
          solution: 'Step 1: Use cosine ratio: cos(E) = adjacent/hypotenuse\nStep 2: Substitute: 0.6 = adjacent/25\nStep 3: Multiply by 25: adjacent = 15',
          answer: '15'
        },
        {
          question: 'A right triangle has an angle of 37° and a hypotenuse of 50. What is the length of the side adjacent to the 37° angle? (Use cos(37°) ≈ 0.8)',
          solution: 'Step 1: Use cosine ratio: cos(37°) = adjacent/hypotenuse\nStep 2: Substitute: 0.8 = adjacent/50\nStep 3: Multiply by 50: adjacent = 40',
          answer: '40'
        },
        {
          question: 'In a right triangle, if sin(F) = 3/5, what is cos(F)? (Hint: Use Pythagorean theorem)',
          solution: 'Step 1: sin(F) = 3/5 means opposite = 3, hypotenuse = 5\nStep 2: Find adjacent using Pythagorean theorem: 3² + adjacent² = 5²\nStep 3: 9 + adjacent² = 25\nStep 4: adjacent² = 16, so adjacent = 4\nStep 5: cos(F) = adjacent/hypotenuse = 4/5',
          answer: '4/5'
        }
      ]
    },
    {
      id: 'trig-ratios-values',
      title: 'Trigonometric Ratios & Special Values',
      standards: ['ACT Math: Trigonometry', 'G.SRT.C.7', 'F.TF.A.3'],
      explanation: 'Certain angles have exact trigonometric values that are important to memorize for the ACT. For 30°: sin(30°) = 1/2, cos(30°) = √3/2, tan(30°) = 1/√3 = √3/3. For 45°: sin(45°) = √2/2, cos(45°) = √2/2, tan(45°) = 1. For 60°: sin(60°) = √3/2, cos(60°) = 1/2, tan(60°) = √3. These values come from the special right triangles: 30-60-90 triangle (sides in ratio 1:√3:2) and 45-45-90 triangle (sides in ratio 1:1:√2). Additionally, for 0° and 90°: sin(0°) = 0, cos(0°) = 1, tan(0°) = 0; sin(90°) = 1, cos(90°) = 0, tan(90°) is undefined. The reciprocal trigonometric functions are: cosecant (csc) = 1/sin, secant (sec) = 1/cos, cotangent (cot) = 1/tan. Understanding these special values and relationships allows you to solve trigonometry problems without a calculator and recognize patterns in trigonometric expressions.',
      questions: [
        {
          question: 'What is the exact value of sin(60°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: sin(60°) = √3/2\nStep 3: This comes from the 30-60-90 triangle.',
          answer: '√3/2'
        },
        {
          question: 'What is the exact value of cos(45°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: cos(45°) = √2/2\nStep 3: This comes from the 45-45-90 triangle.',
          answer: '√2/2'
        },
        {
          question: 'What is the exact value of tan(30°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: tan(30°) = 1/√3\nStep 3: Rationalize: tan(30°) = √3/3',
          answer: '√3/3 or 1/√3'
        },
        {
          question: 'If sin(θ) = 1/2, what is a possible value of θ between 0° and 90°?',
          solution: 'Step 1: Recall which special angle has sin = 1/2.\nStep 2: sin(30°) = 1/2\nStep 3: Therefore, θ = 30°',
          answer: '30°'
        },
        {
          question: 'What is the exact value of tan(45°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: tan(45°) = 1\nStep 3: This is because in a 45-45-90 triangle, opposite = adjacent.',
          answer: '1'
        },
        {
          question: 'What is the exact value of cos(30°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: cos(30°) = √3/2\nStep 3: This comes from the 30-60-90 triangle.',
          answer: '√3/2'
        },
        {
          question: 'If cos(θ) = √2/2, what is a possible value of θ between 0° and 90°?',
          solution: 'Step 1: Recall which special angle has cos = √2/2.\nStep 2: cos(45°) = √2/2\nStep 3: Therefore, θ = 45°',
          answer: '45°'
        },
        {
          question: 'What is the exact value of sin(45°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: sin(45°) = √2/2\nStep 3: This comes from the 45-45-90 triangle.',
          answer: '√2/2'
        },
        {
          question: 'What is the exact value of tan(60°)?',
          solution: 'Step 1: Recall special angle values.\nStep 2: tan(60°) = √3\nStep 3: This comes from the 30-60-90 triangle.',
          answer: '√3'
        },
        {
          question: 'If tan(θ) = √3, what is a possible value of θ between 0° and 90°?',
          solution: 'Step 1: Recall which special angle has tan = √3.\nStep 2: tan(60°) = √3\nStep 3: Therefore, θ = 60°',
          answer: '60°'
        }
      ]
    },
    {
      id: 'solving-right-triangles',
      title: 'Solving Right Triangles',
      standards: ['ACT Math: Trigonometry', 'G.SRT.C.8', 'G.SRT.D.11'],
      explanation: 'Solving a right triangle means finding all unknown sides and angles when given some information. You need at least one side and one other piece of information (another side or an angle). The process involves: (1) Identify what is given and what needs to be found; (2) Choose the appropriate method - use Pythagorean theorem if two sides are known, use trigonometric ratios if an angle and a side are known; (3) Set up the equation correctly; (4) Solve for the unknown; (5) Find remaining parts using additional relationships. Remember that the sum of the two acute angles in a right triangle is 90°, so if you know one acute angle, you can find the other by subtracting from 90°. When using inverse trigonometric functions (sin⁻¹, cos⁻¹, tan⁻¹) on a calculator, make sure your calculator is in degree mode for ACT problems. Always check that your answer makes sense - for example, the hypotenuse must be the longest side, and angles must sum to 180°.',
      questions: [
        {
          question: 'In a right triangle, one acute angle is 35° and the hypotenuse is 20. Find the length of the side opposite the 35° angle.',
          solution: 'Step 1: Use sine ratio: sin(35°) = opposite/20\nStep 2: sin(35°) ≈ 0.574\nStep 3: 0.574 = opposite/20\nStep 4: Multiply by 20: opposite ≈ 11.48',
          answer: 'Approximately 11.48'
        },
        {
          question: 'A right triangle has legs of 9 and 12. Find the measure of the angle opposite the side of length 9.',
          solution: 'Step 1: Find hypotenuse: c² = 9² + 12² = 81 + 144 = 225, so c = 15\nStep 2: Use sine: sin(θ) = 9/15 = 3/5 = 0.6\nStep 3: Use inverse sine: θ = sin⁻¹(0.6)\nStep 4: θ ≈ 36.87°',
          answer: 'Approximately 36.87°'
        },
        {
          question: 'In a right triangle, one angle is 52° and the adjacent side to this angle is 15. Find the hypotenuse.',
          solution: 'Step 1: Use cosine ratio: cos(52°) = 15/hypotenuse\nStep 2: cos(52°) ≈ 0.616\nStep 3: 0.616 = 15/hypotenuse\nStep 4: Cross multiply: 0.616 × hypotenuse = 15\nStep 5: Divide: hypotenuse ≈ 24.35',
          answer: 'Approximately 24.35'
        },
        {
          question: 'A right triangle has one acute angle of 28°. What is the measure of the other acute angle?',
          solution: 'Step 1: The sum of acute angles in a right triangle is 90°.\nStep 2: Set up equation: 28° + x = 90°\nStep 3: Subtract 28°: x = 62°',
          answer: '62°'
        },
        {
          question: 'In a right triangle, the opposite side is 8 and the adjacent side is 15. Find the measure of the angle.',
          solution: 'Step 1: Use tangent: tan(θ) = opposite/adjacent = 8/15\nStep 2: tan(θ) ≈ 0.533\nStep 3: Use inverse tangent: θ = tan⁻¹(0.533)\nStep 4: θ ≈ 28.07°',
          answer: 'Approximately 28.07°'
        },
        {
          question: 'A right triangle has an angle of 40° and the side opposite this angle is 10. Find the adjacent side.',
          solution: 'Step 1: Use tangent ratio: tan(40°) = 10/adjacent\nStep 2: tan(40°) ≈ 0.839\nStep 3: 0.839 = 10/adjacent\nStep 4: Cross multiply: 0.839 × adjacent = 10\nStep 5: Divide: adjacent ≈ 11.92',
          answer: 'Approximately 11.92'
        },
        {
          question: 'In a right triangle, the hypotenuse is 25 and one leg is 15. Find the acute angle opposite the leg of length 15.',
          solution: 'Step 1: Use sine: sin(θ) = 15/25 = 3/5 = 0.6\nStep 2: Use inverse sine: θ = sin⁻¹(0.6)\nStep 3: θ ≈ 36.87°',
          answer: 'Approximately 36.87°'
        },
        {
          question: 'A right triangle has an angle of 65° and a hypotenuse of 30. Find the side adjacent to the 65° angle.',
          solution: 'Step 1: Use cosine ratio: cos(65°) = adjacent/30\nStep 2: cos(65°) ≈ 0.423\nStep 3: 0.423 = adjacent/30\nStep 4: Multiply by 30: adjacent ≈ 12.69',
          answer: 'Approximately 12.69'
        },
        {
          question: 'In a right triangle, one leg is 7 and the hypotenuse is 25. Find the other leg.',
          solution: 'Step 1: Use Pythagorean theorem: 7² + b² = 25²\nStep 2: 49 + b² = 625\nStep 3: Subtract 49: b² = 576\nStep 4: Take square root: b = 24',
          answer: '24'
        },
        {
          question: 'A right triangle has legs in the ratio 3:4 and a hypotenuse of 15. Find the length of the shorter leg.',
          solution: 'Step 1: This is a 3-4-5 triangle scaled up.\nStep 2: Find scale factor: 15 ÷ 5 = 3\nStep 3: Shorter leg = 3 × 3 = 9\nStep 4: Verify: 9² + 12² = 81 + 144 = 225 = 15² ✓',
          answer: '9'
        }
      ]
    },
    {
      id: 'law-of-sines',
      title: 'Law of Sines',
      standards: ['ACT Math: Trigonometry', 'G.SRT.D.10', 'G.SRT.D.11'],
      explanation: 'The Law of Sines applies to any triangle (not just right triangles) and states: a/sin(A) = b/sin(B) = c/sin(C), where a, b, c are side lengths and A, B, C are the angles opposite those sides respectively. This law is useful when you know: (1) Two angles and one side (AAS or ASA), or (2) Two sides and an angle opposite one of them (SSA - ambiguous case). To use the Law of Sines: identify what is given, set up a proportion using two of the three equal ratios, substitute known values, and solve for the unknown. The Law of Sines is particularly helpful for finding missing sides when angles are known, or finding missing angles when sides are known. Remember to use the angle sum property (angles in a triangle sum to 180°) to find a third angle if two are known. On the ACT, Law of Sines problems often involve real-world applications like navigation, surveying, or indirect measurement where right triangles cannot be used.',
      questions: [
        {
          question: 'In triangle ABC, angle A = 40°, angle B = 60°, and side a = 10. Find side b.',
          solution: 'Step 1: Use Law of Sines: a/sin(A) = b/sin(B)\nStep 2: Substitute: 10/sin(40°) = b/sin(60°)\nStep 3: sin(40°) ≈ 0.643, sin(60°) ≈ 0.866\nStep 4: 10/0.643 = b/0.866\nStep 5: Cross multiply: 10(0.866) = 0.643b\nStep 6: 8.66 = 0.643b\nStep 7: Divide: b ≈ 13.47',
          answer: 'Approximately 13.47'
        },
        {
          question: 'In triangle DEF, angle D = 50°, side d = 15, and side e = 12. Find angle E.',
          solution: 'Step 1: Use Law of Sines: d/sin(D) = e/sin(E)\nStep 2: Substitute: 15/sin(50°) = 12/sin(E)\nStep 3: sin(50°) ≈ 0.766\nStep 4: 15/0.766 = 12/sin(E)\nStep 5: Cross multiply: 15 sin(E) = 12(0.766)\nStep 6: 15 sin(E) = 9.192\nStep 7: sin(E) = 0.613\nStep 8: E = sin⁻¹(0.613) ≈ 37.8°',
          answer: 'Approximately 37.8°'
        },
        {
          question: 'In triangle PQR, angle P = 35°, angle Q = 75°, and side p = 8. Find side q.',
          solution: 'Step 1: Use Law of Sines: p/sin(P) = q/sin(Q)\nStep 2: Substitute: 8/sin(35°) = q/sin(75°)\nStep 3: sin(35°) ≈ 0.574, sin(75°) ≈ 0.966\nStep 4: 8/0.574 = q/0.966\nStep 5: Cross multiply: 8(0.966) = 0.574q\nStep 6: 7.728 = 0.574q\nStep 7: Divide: q ≈ 13.46',
          answer: 'Approximately 13.46'
        },
        {
          question: 'In triangle ABC, angle A = 45°, angle C = 60°, and side c = 20. Find angle B.',
          solution: 'Step 1: Use angle sum property: A + B + C = 180°\nStep 2: Substitute: 45° + B + 60° = 180°\nStep 3: 105° + B = 180°\nStep 4: B = 75°',
          answer: '75°'
        },
        {
          question: 'In triangle XYZ, angle X = 30°, side x = 7, and side y = 10. Find angle Y.',
          solution: 'Step 1: Use Law of Sines: x/sin(X) = y/sin(Y)\nStep 2: Substitute: 7/sin(30°) = 10/sin(Y)\nStep 3: sin(30°) = 0.5\nStep 4: 7/0.5 = 10/sin(Y)\nStep 5: 14 = 10/sin(Y)\nStep 6: Cross multiply: 14 sin(Y) = 10\nStep 7: sin(Y) = 10/14 = 5/7 ≈ 0.714\nStep 8: Y = sin⁻¹(0.714) ≈ 45.6°',
          answer: 'Approximately 45.6°'
        },
        {
          question: 'In triangle ABC, angle A = 55°, angle B = 70°, and side a = 18. Find side b.',
          solution: 'Step 1: Use Law of Sines: a/sin(A) = b/sin(B)\nStep 2: Substitute: 18/sin(55°) = b/sin(70°)\nStep 3: sin(55°) ≈ 0.819, sin(70°) ≈ 0.940\nStep 4: 18/0.819 = b/0.940\nStep 5: Cross multiply: 18(0.940) = 0.819b\nStep 6: 16.92 = 0.819b\nStep 7: Divide: b ≈ 20.66',
          answer: 'Approximately 20.66'
        },
        {
          question: 'In triangle DEF, angle D = 48°, side d = 22, and side f = 25. Find angle F.',
          solution: 'Step 1: Use Law of Sines: d/sin(D) = f/sin(F)\nStep 2: Substitute: 22/sin(48°) = 25/sin(F)\nStep 3: sin(48°) ≈ 0.743\nStep 4: 22/0.743 = 25/sin(F)\nStep 5: Cross multiply: 22 sin(F) = 25(0.743)\nStep 6: 22 sin(F) = 18.575\nStep 7: sin(F) = 0.844\nStep 8: F = sin⁻¹(0.844) ≈ 57.6°',
          answer: 'Approximately 57.6°'
        },
        {
          question: 'In triangle PQR, angle P = 25°, angle R = 100°, and side r = 30. Find side p.',
          solution: 'Step 1: Use Law of Sines: p/sin(P) = r/sin(R)\nStep 2: Substitute: p/sin(25°) = 30/sin(100°)\nStep 3: sin(25°) ≈ 0.423, sin(100°) ≈ 0.985\nStep 4: p/0.423 = 30/0.985\nStep 5: Cross multiply: p(0.985) = 30(0.423)\nStep 6: 0.985p = 12.69\nStep 7: Divide: p ≈ 12.88',
          answer: 'Approximately 12.88'
        },
        {
          question: 'In triangle ABC, angle B = 42°, side b = 16, and side c = 20. Find angle C.',
          solution: 'Step 1: Use Law of Sines: b/sin(B) = c/sin(C)\nStep 2: Substitute: 16/sin(42°) = 20/sin(C)\nStep 3: sin(42°) ≈ 0.669\nStep 4: 16/0.669 = 20/sin(C)\nStep 5: Cross multiply: 16 sin(C) = 20(0.669)\nStep 6: 16 sin(C) = 13.38\nStep 7: sin(C) = 0.836\nStep 8: C = sin⁻¹(0.836) ≈ 56.8°',
          answer: 'Approximately 56.8°'
        },
        {
          question: 'In triangle XYZ, angle X = 65°, angle Y = 50°, and side x = 24. Find side z.',
          solution: 'Step 1: Find angle Z: Z = 180° - 65° - 50° = 65°\nStep 2: Use Law of Sines: x/sin(X) = z/sin(Z)\nStep 3: Since X = Z = 65°, sin(X) = sin(Z)\nStep 4: Therefore, x = z\nStep 5: z = 24',
          answer: '24'
        }
      ]
    },
    {
      id: 'law-of-cosines',
      title: 'Law of Cosines',
      standards: ['ACT Math: Trigonometry', 'G.SRT.D.10', 'G.SRT.D.11'],
      explanation: 'The Law of Cosines is a generalization of the Pythagorean theorem that works for any triangle. It states: c² = a² + b² - 2ab cos(C), where c is the side opposite angle C, and a and b are the other two sides. This formula can be rearranged for any side or angle. The Law of Cosines is useful when you know: (1) Three sides (SSS) - to find any angle, or (2) Two sides and the included angle (SAS) - to find the third side. To find an angle when three sides are known, rearrange to: cos(C) = (a² + b² - c²)/(2ab), then use inverse cosine. The Law of Cosines reduces to the Pythagorean theorem when C = 90° (since cos(90°) = 0). On the ACT, this law is essential for solving non-right triangles when the Law of Sines cannot be applied directly. Common applications include navigation problems, force diagrams, and geometric proofs involving triangles.',
      questions: [
        {
          question: 'In triangle ABC, a = 8, b = 10, and angle C = 60°. Find side c.',
          solution: 'Step 1: Use Law of Cosines: c² = a² + b² - 2ab cos(C)\nStep 2: Substitute: c² = 8² + 10² - 2(8)(10)cos(60°)\nStep 3: cos(60°) = 0.5\nStep 4: c² = 64 + 100 - 2(8)(10)(0.5)\nStep 5: c² = 164 - 80 = 84\nStep 6: c = √84 ≈ 9.17',
          answer: 'Approximately 9.17'
        },
        {
          question: 'In triangle DEF, d = 7, e = 9, and f = 12. Find angle F.',
          solution: 'Step 1: Use Law of Cosines: cos(F) = (d² + e² - f²)/(2de)\nStep 2: Substitute: cos(F) = (7² + 9² - 12²)/(2·7·9)\nStep 3: cos(F) = (49 + 81 - 144)/126\nStep 4: cos(F) = -14/126 = -1/9 ≈ -0.111\nStep 5: F = cos⁻¹(-0.111) ≈ 96.4°',
          answer: 'Approximately 96.4°'
        },
        {
          question: 'In triangle PQR, p = 15, q = 20, and angle R = 45°. Find side r.',
          solution: 'Step 1: Use Law of Cosines: r² = p² + q² - 2pq cos(R)\nStep 2: Substitute: r² = 15² + 20² - 2(15)(20)cos(45°)\nStep 3: cos(45°) = √2/2 ≈ 0.707\nStep 4: r² = 225 + 400 - 2(15)(20)(0.707)\nStep 5: r² = 625 - 424.2 = 200.8\nStep 6: r = √200.8 ≈ 14.17',
          answer: 'Approximately 14.17'
        },
        {
          question: 'In triangle ABC, a = 5, b = 7, and c = 8. Find angle A.',
          solution: 'Step 1: Use Law of Cosines: cos(A) = (b² + c² - a²)/(2bc)\nStep 2: Substitute: cos(A) = (7² + 8² - 5²)/(2·7·8)\nStep 3: cos(A) = (49 + 64 - 25)/112\nStep 4: cos(A) = 88/112 = 11/14 ≈ 0.786\nStep 5: A = cos⁻¹(0.786) ≈ 38.2°',
          answer: 'Approximately 38.2°'
        },
        {
          question: 'In triangle XYZ, x = 12, y = 16, and angle Z = 120°. Find side z.',
          solution: 'Step 1: Use Law of Cosines: z² = x² + y² - 2xy cos(Z)\nStep 2: Substitute: z² = 12² + 16² - 2(12)(16)cos(120°)\nStep 3: cos(120°) = -0.5\nStep 4: z² = 144 + 256 - 2(12)(16)(-0.5)\nStep 5: z² = 400 + 192 = 592\nStep 6: z = √592 ≈ 24.33',
          answer: 'Approximately 24.33'
        },
        {
          question: 'In triangle DEF, d = 10, e = 12, and f = 15. Find angle D.',
          solution: 'Step 1: Use Law of Cosines: cos(D) = (e² + f² - d²)/(2ef)\nStep 2: Substitute: cos(D) = (12² + 15² - 10²)/(2·12·15)\nStep 3: cos(D) = (144 + 225 - 100)/360\nStep 4: cos(D) = 269/360 ≈ 0.747\nStep 5: D = cos⁻¹(0.747) ≈ 41.7°',
          answer: 'Approximately 41.7°'
        },
        {
          question: 'In triangle ABC, a = 9, b = 11, and angle C = 75°. Find side c.',
          solution: 'Step 1: Use Law of Cosines: c² = a² + b² - 2ab cos(C)\nStep 2: Substitute: c² = 9² + 11² - 2(9)(11)cos(75°)\nStep 3: cos(75°) ≈ 0.259\nStep 4: c² = 81 + 121 - 2(9)(11)(0.259)\nStep 5: c² = 202 - 51.3 = 150.7\nStep 6: c = √150.7 ≈ 12.28',
          answer: 'Approximately 12.28'
        },
        {
          question: 'In triangle PQR, p = 6, q = 8, and r = 10. Find angle R.',
          solution: 'Step 1: Use Law of Cosines: cos(R) = (p² + q² - r²)/(2pq)\nStep 2: Substitute: cos(R) = (6² + 8² - 10²)/(2·6·8)\nStep 3: cos(R) = (36 + 64 - 100)/96\nStep 4: cos(R) = 0/96 = 0\nStep 5: R = cos⁻¹(0) = 90°\nStep 6: This is a right triangle (3-4-5 scaled by 2).',
          answer: '90°'
        },
        {
          question: 'In triangle ABC, a = 14, b = 18, and c = 20. Find angle B.',
          solution: 'Step 1: Use Law of Cosines: cos(B) = (a² + c² - b²)/(2ac)\nStep 2: Substitute: cos(B) = (14² + 20² - 18²)/(2·14·20)\nStep 3: cos(B) = (196 + 400 - 324)/560\nStep 4: cos(B) = 272/560 = 17/35 ≈ 0.486\nStep 5: B = cos⁻¹(0.486) ≈ 60.9°',
          answer: 'Approximately 60.9°'
        },
        {
          question: 'In triangle XYZ, x = 5, y = 7, and angle Z = 90°. Find side z using the Law of Cosines.',
          solution: 'Step 1: Use Law of Cosines: z² = x² + y² - 2xy cos(Z)\nStep 2: Substitute: z² = 5² + 7² - 2(5)(7)cos(90°)\nStep 3: cos(90°) = 0\nStep 4: z² = 25 + 49 - 0 = 74\nStep 5: z = √74 ≈ 8.60\nStep 6: Note: This reduces to Pythagorean theorem when angle is 90°.',
          answer: '√74 or approximately 8.60'
        }
      ]
    },
    {
      id: 'unit-circle-basics',
      title: 'Unit Circle Basics',
      standards: ['ACT Math: Trigonometry', 'F.TF.A.2', 'F.TF.A.3'],
      explanation: 'The unit circle is a circle with radius 1 centered at the origin of a coordinate plane. It provides a geometric interpretation of trigonometric functions for all angles. On the unit circle, any point (x, y) corresponds to an angle θ measured counterclockwise from the positive x-axis, where x = cos(θ) and y = sin(θ). Key reference angles and their coordinates include: 0° (1, 0), 30° (√3/2, 1/2), 45° (√2/2, √2/2), 60° (1/2, √3/2), 90° (0, 1). The unit circle extends trigonometry beyond acute angles to include angles greater than 90° and negative angles. Angles in different quadrants have different sign patterns: Quadrant I (0°-90°): both positive; Quadrant II (90°-180°): sin positive, cos negative; Quadrant III (180°-270°): both negative; Quadrant IV (270°-360°): cos positive, sin negative. The mnemonic "All Students Take Calculus" helps remember which functions are positive in each quadrant (All, Sine, Tangent, Cosine). Understanding the unit circle is essential for evaluating trigonometric functions at various angles.',
      questions: [
        {
          question: 'On the unit circle, what are the coordinates of the point at 0°?',
          solution: 'Step 1: At 0°, the point is on the positive x-axis.\nStep 2: The coordinates are (cos(0°), sin(0°)).\nStep 3: cos(0°) = 1, sin(0°) = 0\nStep 4: Coordinates: (1, 0)',
          answer: '(1, 0)'
        },
        {
          question: 'On the unit circle, what are the coordinates of the point at 90°?',
          solution: 'Step 1: At 90°, the point is on the positive y-axis.\nStep 2: The coordinates are (cos(90°), sin(90°)).\nStep 3: cos(90°) = 0, sin(90°) = 1\nStep 4: Coordinates: (0, 1)',
          answer: '(0, 1)'
        },
        {
          question: 'What is the value of sin(180°)?',
          solution: 'Step 1: At 180°, the point is on the negative x-axis.\nStep 2: Coordinates are (-1, 0).\nStep 3: sin(180°) is the y-coordinate.\nStep 4: sin(180°) = 0',
          answer: '0'
        },
        {
          question: 'What is the value of cos(270°)?',
          solution: 'Step 1: At 270°, the point is on the negative y-axis.\nStep 2: Coordinates are (0, -1).\nStep 3: cos(270°) is the x-coordinate.\nStep 4: cos(270°) = 0',
          answer: '0'
        },
        {
          question: 'In which quadrant is the angle 150°, and what are the signs of sin(150°) and cos(150°)?',
          solution: 'Step 1: 150° is between 90° and 180°, so it is in Quadrant II.\nStep 2: In Quadrant II, sine is positive and cosine is negative.\nStep 3: sin(150°) > 0, cos(150°) < 0',
          answer: 'Quadrant II; sin is positive, cos is negative'
        },
        {
          question: 'What is the value of sin(45°) on the unit circle?',
          solution: 'Step 1: At 45°, the coordinates are (√2/2, √2/2).\nStep 2: sin(45°) is the y-coordinate.\nStep 3: sin(45°) = √2/2',
          answer: '√2/2'
        },
        {
          question: 'What is the value of cos(60°) on the unit circle?',
          solution: 'Step 1: At 60°, the coordinates are (1/2, √3/2).\nStep 2: cos(60°) is the x-coordinate.\nStep 3: cos(60°) = 1/2',
          answer: '1/2'
        },
        {
          question: 'In which quadrant is the angle 225°, and what are the signs of sin(225°) and cos(225°)?',
          solution: 'Step 1: 225° is between 180° and 270°, so it is in Quadrant III.\nStep 2: In Quadrant III, both sine and cosine are negative.\nStep 3: sin(225°) < 0, cos(225°) < 0',
          answer: 'Quadrant III; both sin and cos are negative'
        },
        {
          question: 'What is the value of tan(0°)?',
          solution: 'Step 1: tan(θ) = sin(θ)/cos(θ)\nStep 2: tan(0°) = sin(0°)/cos(0°) = 0/1\nStep 3: tan(0°) = 0',
          answer: '0'
        },
        {
          question: 'On the unit circle, if a point has coordinates (-1/2, √3/2), what angle does it represent (between 0° and 360°)?',
          solution: 'Step 1: The x-coordinate is negative and y-coordinate is positive, so the angle is in Quadrant II.\nStep 2: The reference angle has cos = 1/2 and sin = √3/2, which is 60°.\nStep 3: In Quadrant II, the angle is 180° - 60° = 120°.',
          answer: '120°'
        }
      ]
    },
    {
      id: 'trig-identities',
      title: 'Trigonometric Identities',
      standards: ['ACT Math: Trigonometry', 'F.TF.C.8', 'F.TF.C.9'],
      explanation: 'Trigonometric identities are equations involving trigonometric functions that are true for all values of the variables. The most fundamental is the Pythagorean identity: sin²(θ) + cos²(θ) = 1, which comes directly from the unit circle. From this, we can derive: tan²(θ) + 1 = sec²(θ) and 1 + cot²(θ) = csc²(θ). Reciprocal identities include: csc(θ) = 1/sin(θ), sec(θ) = 1/cos(θ), cot(θ) = 1/tan(θ). Quotient identities are: tan(θ) = sin(θ)/cos(θ) and cot(θ) = cos(θ)/sin(θ). Even-odd identities: sin(-θ) = -sin(θ), cos(-θ) = cos(θ), tan(-θ) = -tan(θ). Cofunction identities relate complementary angles: sin(90° - θ) = cos(θ), cos(90° - θ) = sin(θ), tan(90° - θ) = cot(θ). On the ACT, you may need to simplify expressions, verify identities, or solve equations using these relationships. Understanding these identities helps you manipulate trigonometric expressions and solve more complex problems efficiently.',
      questions: [
        {
          question: 'If sin(θ) = 3/5, what is cos²(θ)? (Use the Pythagorean identity)',
          solution: 'Step 1: Use Pythagorean identity: sin²(θ) + cos²(θ) = 1\nStep 2: Substitute: (3/5)² + cos²(θ) = 1\nStep 3: 9/25 + cos²(θ) = 1\nStep 4: cos²(θ) = 1 - 9/25 = 16/25',
          answer: '16/25'
        },
        {
          question: 'Simplify: sin(θ)/cos(θ)',
          solution: 'Step 1: Use quotient identity: sin(θ)/cos(θ) = tan(θ)\nStep 2: The simplified form is tan(θ).',
          answer: 'tan(θ)'
        },
        {
          question: 'If cos(θ) = 4/5 and θ is in Quadrant I, find sin(θ).',
          solution: 'Step 1: Use Pythagorean identity: sin²(θ) + cos²(θ) = 1\nStep 2: sin²(θ) + (4/5)² = 1\nStep 3: sin²(θ) + 16/25 = 1\nStep 4: sin²(θ) = 9/25\nStep 5: sin(θ) = 3/5 (positive in Quadrant I)',
          answer: '3/5'
        },
        {
          question: 'What is the value of sin²(30°) + cos²(30°)?',
          solution: 'Step 1: Use Pythagorean identity: sin²(θ) + cos²(θ) = 1\nStep 2: This is true for any angle θ.\nStep 3: sin²(30°) + cos²(30°) = 1',
          answer: '1'
        },
        {
          question: 'If tan(θ) = 2, what is cot(θ)?',
          solution: 'Step 1: Use reciprocal identity: cot(θ) = 1/tan(θ)\nStep 2: Substitute: cot(θ) = 1/2',
          answer: '1/2'
        },
        {
          question: 'Simplify: 1/sin(θ)',
          solution: 'Step 1: Use reciprocal identity: 1/sin(θ) = csc(θ)\nStep 2: The simplified form is csc(θ).',
          answer: 'csc(θ)'
        },
        {
          question: 'What is sin(90° - 40°)?',
          solution: 'Step 1: Use cofunction identity: sin(90° - θ) = cos(θ)\nStep 2: sin(90° - 40°) = cos(40°)',
          answer: 'cos(40°)'
        },
        {
          question: 'If sin(θ) = 0.6, what is sin(-θ)?',
          solution: 'Step 1: Use even-odd identity: sin(-θ) = -sin(θ)\nStep 2: Substitute: sin(-θ) = -0.6',
          answer: '-0.6'
        },
        {
          question: 'Simplify: cos(θ)/sin(θ)',
          solution: 'Step 1: Use quotient identity: cos(θ)/sin(θ) = cot(θ)\nStep 2: The simplified form is cot(θ).',
          answer: 'cot(θ)'
        },
        {
          question: 'If cos(θ) = 5/13 and θ is in Quadrant IV, find sin(θ).',
          solution: 'Step 1: Use Pythagorean identity: sin²(θ) + cos²(θ) = 1\nStep 2: sin²(θ) + (5/13)² = 1\nStep 3: sin²(θ) + 25/169 = 1\nStep 4: sin²(θ) = 144/169\nStep 5: sin(θ) = -12/13 (negative in Quadrant IV)',
          answer: '-12/13'
        }
      ]
    },
    {
      id: 'graphing-trig-functions',
      title: 'Graphing Trigonometric Functions',
      standards: ['ACT Math: Trigonometry', 'F.TF.B.5', 'F.IF.C.7e'],
      explanation: 'Trigonometric functions have characteristic graphs with specific properties. The sine function y = sin(x) oscillates between -1 and 1, has a period of 360° (or 2π radians), and passes through the origin. The cosine function y = cos(x) also oscillates between -1 and 1 with period 360°, but starts at its maximum value of 1 when x = 0. The tangent function y = tan(x) has a period of 180° (or π radians), has vertical asymptotes where cos(x) = 0, and ranges from negative infinity to positive infinity. Transformations of these functions follow the general form y = A·sin(B(x - C)) + D or y = A·cos(B(x - C)) + D, where: |A| is the amplitude (vertical stretch), B affects the period (new period = 360°/B), C is the horizontal shift (phase shift), and D is the vertical shift. For tangent, there is no amplitude since it has no maximum or minimum. On the ACT, you may need to identify key features of trigonometric graphs (amplitude, period, phase shift, vertical shift), match equations to graphs, or determine equations from graphs.',
      questions: [
        {
          question: 'What is the amplitude of y = 3sin(x)?',
          solution: 'Step 1: The amplitude is the coefficient of the sine function.\nStep 2: Amplitude = |A| = |3| = 3\nStep 3: The graph oscillates between -3 and 3.',
          answer: '3'
        },
        {
          question: 'What is the period of y = sin(2x)?',
          solution: 'Step 1: The period of sin(Bx) is 360°/B (or 2π/B in radians).\nStep 2: Here, B = 2.\nStep 3: Period = 360°/2 = 180° (or π radians)',
          answer: '180° or π radians'
        },
        {
          question: 'What is the period of y = cos(x/2)?',
          solution: 'Step 1: The period of cos(Bx) is 360°/B.\nStep 2: Here, B = 1/2.\nStep 3: Period = 360°/(1/2) = 720° (or 4π radians)',
          answer: '720° or 4π radians'
        },
        {
          question: 'What is the amplitude of y = -5cos(x)?',
          solution: 'Step 1: The amplitude is the absolute value of the coefficient.\nStep 2: Amplitude = |-5| = 5\nStep 3: The negative sign reflects the graph, but amplitude is always positive.',
          answer: '5'
        },
        {
          question: 'For y = sin(x) + 2, what is the vertical shift?',
          solution: 'Step 1: The vertical shift is the constant added to the function.\nStep 2: Vertical shift = D = 2\nStep 3: The graph is shifted up 2 units.',
          answer: '2 units up'
        },
        {
          question: 'What is the period of y = tan(x)?',
          solution: 'Step 1: The period of tan(x) is 180° (or π radians).\nStep 2: This is different from sine and cosine, which have period 360°.',
          answer: '180° or π radians'
        },
        {
          question: 'For y = 2sin(3x), what is the amplitude and period?',
          solution: 'Step 1: Amplitude = |A| = |2| = 2\nStep 2: Period = 360°/B = 360°/3 = 120° (or 2π/3 radians)\nStep 3: Amplitude is 2, period is 120°.',
          answer: 'Amplitude: 2; Period: 120° or 2π/3 radians'
        },
        {
          question: 'What is the maximum value of y = 4cos(x) - 1?',
          solution: 'Step 1: The maximum value of cos(x) is 1.\nStep 2: Maximum of 4cos(x) is 4(1) = 4.\nStep 3: With vertical shift: 4 - 1 = 3',
          answer: '3'
        },
        {
          question: 'For y = sin(x - 90°), what is the phase shift?',
          solution: 'Step 1: The phase shift is C in the form sin(x - C).\nStep 2: Phase shift = 90° to the right.\nStep 3: The graph is shifted 90° to the right.',
          answer: '90° to the right'
        },
        {
          question: 'What is the minimum value of y = -3sin(x) + 2?',
          solution: 'Step 1: The minimum value of sin(x) is -1.\nStep 2: Minimum of -3sin(x) is -3(-1) = 3.\nStep 3: With vertical shift: 3 + 2 = 5\nStep 4: Wait, recalculate: Maximum of -3sin(x) is 3, minimum is -3.\nStep 5: Minimum of -3sin(x) + 2 is -3 + 2 = -1',
          answer: '-1'
        }
      ]
    },
    {
      id: 'inverse-trig-functions',
      title: 'Inverse Trigonometric Functions',
      standards: ['ACT Math: Trigonometry', 'F.TF.B.6', 'F.TF.B.7'],
      explanation: 'Inverse trigonometric functions reverse the process of trigonometric functions - they take a ratio and return an angle. The three main inverse functions are: arcsin (or sin⁻¹), arccos (or cos⁻¹), and arctan (or tan⁻¹). For example, if sin(30°) = 0.5, then sin⁻¹(0.5) = 30°. The domains and ranges are restricted to ensure the inverse functions are well-defined: sin⁻¹(x) has domain [-1, 1] and range [-90°, 90°]; cos⁻¹(x) has domain [-1, 1] and range [0°, 180°]; tan⁻¹(x) has domain (-∞, ∞) and range (-90°, 90°). When using a calculator, ensure it is in degree mode for ACT problems. Inverse trig functions are used to find angles when side ratios are known in right triangles, or to solve trigonometric equations. On the ACT, you may need to evaluate inverse trig functions, solve equations involving them, or apply them to find angles in geometric or real-world contexts. Remember that sin⁻¹ does not mean 1/sin - it means the inverse function.',
      questions: [
        {
          question: 'What is sin⁻¹(1/2)?',
          solution: 'Step 1: Find the angle whose sine is 1/2.\nStep 2: Recall that sin(30°) = 1/2.\nStep 3: sin⁻¹(1/2) = 30°',
          answer: '30°'
        },
        {
          question: 'What is cos⁻¹(0)?',
          solution: 'Step 1: Find the angle whose cosine is 0.\nStep 2: Recall that cos(90°) = 0.\nStep 3: cos⁻¹(0) = 90°',
          answer: '90°'
        },
        {
          question: 'What is tan⁻¹(1)?',
          solution: 'Step 1: Find the angle whose tangent is 1.\nStep 2: Recall that tan(45°) = 1.\nStep 3: tan⁻¹(1) = 45°',
          answer: '45°'
        },
        {
          question: 'What is sin⁻¹(√3/2)?',
          solution: 'Step 1: Find the angle whose sine is √3/2.\nStep 2: Recall that sin(60°) = √3/2.\nStep 3: sin⁻¹(√3/2) = 60°',
          answer: '60°'
        },
        {
          question: 'What is cos⁻¹(√2/2)?',
          solution: 'Step 1: Find the angle whose cosine is √2/2.\nStep 2: Recall that cos(45°) = √2/2.\nStep 3: cos⁻¹(√2/2) = 45°',
          answer: '45°'
        },
        {
          question: 'In a right triangle, if the opposite side is 8 and the hypotenuse is 10, what is the angle? (Use sin⁻¹)',
          solution: 'Step 1: sin(θ) = opposite/hypotenuse = 8/10 = 0.8\nStep 2: θ = sin⁻¹(0.8)\nStep 3: θ ≈ 53.13°',
          answer: 'Approximately 53.13°'
        },
        {
          question: 'What is tan⁻¹(√3)?',
          solution: 'Step 1: Find the angle whose tangent is √3.\nStep 2: Recall that tan(60°) = √3.\nStep 3: tan⁻¹(√3) = 60°',
          answer: '60°'
        },
        {
          question: 'What is sin⁻¹(-1)?',
          solution: 'Step 1: Find the angle whose sine is -1.\nStep 2: Recall that sin(-90°) = -1 or sin(270°) = -1.\nStep 3: The range of sin⁻¹ is [-90°, 90°].\nStep 4: sin⁻¹(-1) = -90°',
          answer: '-90°'
        },
        {
          question: 'In a right triangle, if the adjacent side is 5 and the hypotenuse is 13, what is the angle? (Use cos⁻¹)',
          solution: 'Step 1: cos(θ) = adjacent/hypotenuse = 5/13 ≈ 0.385\nStep 2: θ = cos⁻¹(0.385)\nStep 3: θ ≈ 67.38°',
          answer: 'Approximately 67.38°'
        },
        {
          question: 'What is cos⁻¹(1)?',
          solution: 'Step 1: Find the angle whose cosine is 1.\nStep 2: Recall that cos(0°) = 1.\nStep 3: cos⁻¹(1) = 0°',
          answer: '0°'
        }
      ]
    },
    {
      id: 'trig-applications',
      title: 'Trigonometry Applications & Word Problems',
      standards: ['ACT Math: Trigonometry', 'G.SRT.D.11', 'F.TF.B.5'],
      explanation: 'Trigonometry has numerous real-world applications that frequently appear on the ACT. Common scenarios include: (1) Angle of elevation/depression - the angle from horizontal up to an object (elevation) or down to an object (depression); (2) Navigation and bearings - using angles and distances to determine positions; (3) Height and distance problems - finding inaccessible heights or distances using trigonometric ratios; (4) Projectile motion - analyzing trajectories using angles; (5) Periodic phenomena - modeling waves, tides, or cyclical patterns with sine and cosine functions. Problem-solving strategy: (1) Draw a diagram showing the situation; (2) Identify the right triangle(s) or use appropriate trigonometric laws; (3) Label known and unknown quantities; (4) Choose the correct trigonometric ratio or formula; (5) Set up and solve the equation; (6) Check that your answer makes sense in context. Pay attention to units and whether angles should be in degrees or radians. These problems test your ability to translate real-world situations into mathematical models and apply trigonometric concepts to solve practical problems.',
      questions: [
        {
          question: 'A person standing 50 feet from a building measures the angle of elevation to the top as 60°. How tall is the building?',
          solution: 'Step 1: Draw a right triangle with adjacent side 50 ft and angle 60°.\nStep 2: Use tangent: tan(60°) = height/50\nStep 3: tan(60°) = √3 ≈ 1.732\nStep 4: height = 50 × 1.732 ≈ 86.6 feet',
          answer: 'Approximately 86.6 feet or 50√3 feet'
        },
        {
          question: 'From the top of a 100-foot lighthouse, the angle of depression to a boat is 25°. How far is the boat from the base of the lighthouse?',
          solution: 'Step 1: Angle of depression from top = angle of elevation from boat = 25°.\nStep 2: Use tangent: tan(25°) = 100/distance\nStep 3: tan(25°) ≈ 0.466\nStep 4: distance = 100/0.466 ≈ 214.6 feet',
          answer: 'Approximately 214.6 feet'
        },
        {
          question: 'A ladder 20 feet long leans against a wall at an angle of 70° with the ground. How high up the wall does the ladder reach?',
          solution: 'Step 1: The ladder is the hypotenuse, height is opposite the 70° angle.\nStep 2: Use sine: sin(70°) = height/20\nStep 3: sin(70°) ≈ 0.940\nStep 4: height = 20 × 0.940 ≈ 18.8 feet',
          answer: 'Approximately 18.8 feet'
        },
        {
          question: 'A kite is flying at a height of 80 feet. The string makes an angle of 50° with the ground. How long is the string?',
          solution: 'Step 1: Height is opposite the angle, string is hypotenuse.\nStep 2: Use sine: sin(50°) = 80/string\nStep 3: sin(50°) ≈ 0.766\nStep 4: string = 80/0.766 ≈ 104.4 feet',
          answer: 'Approximately 104.4 feet'
        },
        {
          question: 'A ramp rises 3 feet over a horizontal distance of 15 feet. What angle does the ramp make with the ground?',
          solution: 'Step 1: Rise is opposite, horizontal distance is adjacent.\nStep 2: Use tangent: tan(θ) = 3/15 = 0.2\nStep 3: θ = tan⁻¹(0.2)\nStep 4: θ ≈ 11.31°',
          answer: 'Approximately 11.31°'
        },
        {
          question: 'Two buildings are 100 feet apart. From the top of the shorter building (40 feet tall), the angle of elevation to the top of the taller building is 30°. How tall is the taller building?',
          solution: 'Step 1: The height difference is opposite the 30° angle, distance is adjacent (100 ft).\nStep 2: Use tangent: tan(30°) = height difference/100\nStep 3: tan(30°) = 1/√3 ≈ 0.577\nStep 4: height difference = 100 × 0.577 ≈ 57.7 feet\nStep 5: Total height = 40 + 57.7 ≈ 97.7 feet',
          answer: 'Approximately 97.7 feet'
        },
        {
          question: 'A surveyor stands 120 meters from the base of a mountain. The angle of elevation to the peak is 35°. How high is the mountain?',
          solution: 'Step 1: Use tangent: tan(35°) = height/120\nStep 2: tan(35°) ≈ 0.700\nStep 3: height = 120 × 0.700 ≈ 84 meters',
          answer: 'Approximately 84 meters'
        },
        {
          question: 'A plane takes off at an angle of 15° and travels 2000 feet along its flight path. What is its altitude?',
          solution: 'Step 1: Altitude is opposite the 15° angle, flight path is hypotenuse.\nStep 2: Use sine: sin(15°) = altitude/2000\nStep 3: sin(15°) ≈ 0.259\nStep 4: altitude = 2000 × 0.259 ≈ 518 feet',
          answer: 'Approximately 518 feet'
        },
        {
          question: 'A tree casts a shadow 30 feet long when the angle of elevation of the sun is 55°. How tall is the tree?',
          solution: 'Step 1: Shadow is adjacent, tree height is opposite the 55° angle.\nStep 2: Use tangent: tan(55°) = height/30\nStep 3: tan(55°) ≈ 1.428\nStep 4: height = 30 × 1.428 ≈ 42.8 feet',
          answer: 'Approximately 42.8 feet'
        },
        {
          question: 'A cable car travels 500 meters up a mountain slope at an angle of 40°. What is the vertical height gained?',
          solution: 'Step 1: Vertical height is opposite the 40° angle, cable length is hypotenuse.\nStep 2: Use sine: sin(40°) = height/500\nStep 3: sin(40°) ≈ 0.643\nStep 4: height = 500 × 0.643 ≈ 321.5 meters',
          answer: 'Approximately 321.5 meters'
        }
      ]
    }
  ]
};